/* eslint-disable complexity */
import React from 'react';
import clsx from 'clsx';

import { CELL_WIDTH, PeriodType } from '../../../core/constants';
import { DataGridContext, IDataGridContext } from '../../../forecast/detail/reports/data-grid/context/data-grid';
import { DIRECTION, PeriodTableControl } from '../../../forecast/detail/components/period-menu/period-table-control';
import { Page } from '../../../core/types';
import { useDataGridLayout } from './data-grid-layout-context';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';

import grid from './grid.scss';

/* eslint-disable no-magic-numbers */
const SCROLL_CELL_THRESHOLD = 2 * CELL_WIDTH;
const INITIAL_SCROLL_OFFSET = 2.5;
const SCROLL_SPEED = 1.2;
const SCROLL_DISTANCE_THRESHOLD = 10;
const SCROLL_TABLE_CONTROLS_THRESHOLD = 6 * CELL_WIDTH;
/* eslint-enable no-magic-numbers */

interface Props {
  headers?: JSX.Element;
  isSticky?: boolean;
  onScroll?: (scrollLeft: number) => void;
  onScrollEnd?: (page?: Page) => void;
  rows?: JSX.Element;
  tableForwardRef: React.RefObject<HTMLDivElement>;
  isLoading?: boolean;
  totalPastDates?: number;
  disablePastScrolling?: boolean;
  periodType?: PeriodType;
  showTableControls?: boolean;
  isMainHeadlineGrid?: boolean;
  hideBudgetData?: boolean;
}

export const DataGrid: React.FC<Props> = props => {
  const {
    disablePastScrolling,
    headers,
    isLoading,
    rows,
    tableForwardRef,
    totalPastDates,
    periodType,
    showTableControls = true,
    isMainHeadlineGrid,
    hideBudgetData = false
  } = props;

  const [hasScrolled, setHasScrolled] = React.useState(false);
  const [isDown, setIsDown] = React.useState(false);
  const [startX, setStartX] = React.useState(0);
  const [scrollLeft, setScrollLeft] = React.useState(0);

  const { rowHeaderWidth } = usePredictionsInfoContext();
  const dataGridLayout = useDataGridLayout();

  const dataGridContextValue = React.useMemo(
    (): Partial<IDataGridContext> => ({
      hasScrolled,
      tableRef: tableForwardRef
    }),
    [hasScrolled, tableForwardRef]
  );

  // on mount or period change set the initial scroll left
  // note this should only trigger for one data grid on each page
  React.useEffect(() => {
    if (dataGridLayout?.fitsInViewport) {
      if (tableForwardRef.current) {
        tableForwardRef.current.scrollLeft = 0;
      }
      return;
    }

    if (!totalPastDates) {
      return;
    }

    if (!disablePastScrolling) {
      // datagrid has past columns so reset position to desired initial offset
      tableForwardRef.current!.scrollLeft = (totalPastDates! - INITIAL_SCROLL_OFFSET) * CELL_WIDTH;
    } else {
      // datagrid has no past columns so reset to furthest left (current month/week)
      tableForwardRef.current!.scrollLeft = 0;
    }
  }, [periodType, totalPastDates, disablePastScrolling, dataGridLayout?.fitsInViewport]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const { pageX, clientX, currentTarget } = event;

    setIsDown(true);
    setStartX((pageX || clientX) - currentTarget.offsetLeft);
    setScrollLeft(currentTarget.scrollLeft);
  };

  const handleMouseUp = () => setIsDown(false);

  const handleMouseLeave = () => setIsDown(false);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!isDown) {
      return;
    }

    event.preventDefault();

    const { pageX, clientX, currentTarget } = event;
    const currentXPos = (pageX || clientX) - currentTarget.offsetLeft;
    const walk = (currentXPos - startX) * SCROLL_SPEED;

    currentTarget.scrollLeft = scrollLeft - walk;
  };

  const handleScroll = (event: React.UIEvent): void => {
    const { scrollLeft, scrollWidth, clientWidth } = event.currentTarget;
    const { onScroll } = props;

    const { hasScrolled } = getDataGridScrollValues(scrollLeft, scrollWidth, clientWidth);

    onScroll && onScroll(scrollLeft);

    setHasScrolled(hasScrolled);
  };

  const handlePeriodTableControl = (direction: DIRECTION) => () => {
    const elmt = tableForwardRef.current,
      time = 500;
    if (!elmt) {
      return;
    }
    const currentPos = elmt.scrollLeft ?? 0;
    const pos =
      direction === DIRECTION.back
        ? currentPos - SCROLL_TABLE_CONTROLS_THRESHOLD
        : currentPos + SCROLL_TABLE_CONTROLS_THRESHOLD;
    let start = 0;
    const step = (currentTime: number) => {
      start = !start ? currentTime : start;
      const progress = currentTime - start;
      if (currentPos < pos) {
        elmt.scrollTo({ left: ((pos - currentPos) * progress) / time + currentPos });
      } else {
        elmt.scrollTo({ left: currentPos - ((currentPos - pos) * progress) / time });
      }
      if (progress < time) {
        window.requestAnimationFrame(step);
      } else {
        elmt.scrollTo({ left: pos });
      }
    };
    window.requestAnimationFrame(step);
  };

  const wrapperClasses = clsx({
    [grid.wrapper]: true,
    [grid.isLoading]: isLoading,
    [grid.isMainHeadlineGrid]: isMainHeadlineGrid
  });

  const tableInnerClasses = clsx({
    [grid.tableInner]: true,
    [grid.isDown]: isDown
  });

  const layoutStyles = dataGridLayout
    ? ({
        '--data-grid-column-width': `${dataGridLayout.columnWidth}px`,
        '--data-grid-content-width': `${dataGridLayout.contentWidth}px`
      } as React.CSSProperties)
    : undefined;

  return (
    <DataGridContext.Provider value={dataGridContextValue}>
      <div className={wrapperClasses} style={layoutStyles}>
        {!hideBudgetData && headers}
        <div className={grid.table} style={{ marginLeft: rowHeaderWidth }}>
          <div
            className={tableInnerClasses}
            onScroll={handleScroll}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            ref={tableForwardRef}
            data-testid="datagrid-table"
            tabIndex={0}
          >
            <div className={grid.tableContent}>{rows}</div>
          </div>
        </div>
        {showTableControls && (
          <PeriodTableControl
            onBackClick={handlePeriodTableControl(DIRECTION.back)}
            onForwardClick={handlePeriodTableControl(DIRECTION.forward)}
          />
        )}
      </div>
    </DataGridContext.Provider>
  );
};

/**
 * Gets the values of the data-grid after an scroll happens
 * @param {number} scrollLeft - it the number of pixels that an element's content is scrolled from its left edge
 * @param {number} scrollWidth - is the total width including stuff that is only visible if you scroll
 * @param {number} clientWidth - is the inner width (ie. the space inside an element including padding but excluding borders and scrollbars)
 * @return {object}
 */
export const getDataGridScrollValues = (scrollLeft: number, scrollWidth: number, clientWidth: number) => {
  const hasScrolled = scrollLeft > SCROLL_DISTANCE_THRESHOLD;
  const scrolledOffset = scrollWidth - clientWidth - scrollLeft;
  const isNearEnd = scrolledOffset < SCROLL_CELL_THRESHOLD;
  const isNearStart = scrollLeft < SCROLL_CELL_THRESHOLD;

  return {
    hasScrolled,
    isNearEnd,
    isNearStart
  };
};
